import type {
  AttachmentDto,
  AttachmentKind,
  DocumentDto,
  MediaAssetDto,
  Paged,
} from '@ukinfo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Checkbox, Input, cn } from './ui';

/** Ein gewaehlter Anhang - der Titel wird mitgefuehrt, damit er auch ohne Suchtreffer sichtbar bleibt. */
export interface AttachmentChoice {
  kind: AttachmentKind;
  refId: string;
  title: string;
}

export const toChoices = (attachments: AttachmentDto[]): AttachmentChoice[] =>
  attachments.map((a) => ({ kind: a.kind, refId: a.refId, title: a.title }));

export const toRefs = (choices: AttachmentChoice[]): { kind: AttachmentKind; refId: string }[] =>
  choices.map(({ kind, refId }) => ({ kind, refId }));

const KIND_LABELS: Record<AttachmentKind, string> = { MEDIA: 'Medien', DOCUMENT: 'Dokumente' };
const KIND_ICONS: Record<AttachmentKind, string> = { MEDIA: '🖼️', DOCUMENT: '📄' };

function formatSize(bytes: number | null): string | null {
  if (bytes === null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Dateien aus der Mediathek oder aus Wissen & Dokumente auswaehlen.
 *
 * Gesucht wird auf dem Server, angezeigt nur ein Ausschnitt. Gewaehlte Dateien
 * stehen oben und bleiben stehen, auch wenn sie nicht zur Suche passen - sonst
 * liessen sie sich nicht mehr abwaehlen.
 */
export function AttachmentPicker({
  id,
  value,
  onChange,
}: {
  id?: string;
  value: AttachmentChoice[];
  onChange: (value: AttachmentChoice[]) => void;
}): JSX.Element {
  const { can } = useAuth();
  const kinds = ([
    can('media', 'read') ? 'MEDIA' : null,
    can('documents', 'read') ? 'DOCUMENT' : null,
  ] as (AttachmentKind | null)[]).filter((k): k is AttachmentKind => k !== null);
  const [kind, setKind] = useState<AttachmentKind>(kinds[0] ?? 'MEDIA');
  const [search, setSearch] = useState('');

  const query = { ...(search.trim() ? { search: search.trim() } : {}), pageSize: 25 };
  const treffer = useQuery({
    queryKey: ['attachment-picker', kind, query],
    queryFn: async (): Promise<{ items: { id: string; title: string; hint: string | null }[]; total: number }> => {
      if (kind === 'MEDIA') {
        const page = await api.get<Paged<MediaAssetDto>>('/api/media/assets', query);
        return { total: page.total, items: page.items.map((a) => ({ id: a.id, title: a.title, hint: a.fileName })) };
      }
      const page = await api.get<Paged<DocumentDto>>('/api/documents', query);
      return { total: page.total, items: page.items.map((d) => ({ id: d.id, title: d.title, hint: d.fileName })) };
    },
    enabled: kinds.includes(kind),
    placeholderData: (vorher) => vorher,
  });

  const isChosen = (refId: string): boolean => value.some((v) => v.kind === kind && v.refId === refId);
  const others = (treffer.data?.items ?? []).filter((item) => !isChosen(item.id));

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <ul className="space-y-1 rounded-lg border border-slate-200 bg-white p-2">
          {value.map((choice) => (
            <li key={`${choice.kind}:${choice.refId}`}>
              <Checkbox
                label={<span>{KIND_ICONS[choice.kind]} {choice.title}</span>}
                checked
                onChange={() => onChange(value.filter((v) => !(v.kind === choice.kind && v.refId === choice.refId)))}
              />
            </li>
          ))}
        </ul>
      )}

      {kinds.length === 0 ? (
        <p className="text-sm text-slate-500">Zum Anhängen fehlt dir der Zugriff auf Medien und Dokumente.</p>
      ) : (
      <>
      <div className="flex flex-wrap gap-2">
        {kinds.length > 1 && (
          <div className="inline-flex rounded-lg border border-slate-300 p-0.5" role="group" aria-label="Quelle">
            {kinds.map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={kind === k}
                onClick={() => setKind(k)}
                className={cn(
                  'rounded-md px-3 py-1 text-sm',
                  kind === k ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100',
                )}
              >
                {KIND_LABELS[k]}
              </button>
            ))}
          </div>
        )}
        <Input
          id={id}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={kind === 'MEDIA' ? 'In der Mediathek suchen …' : 'In den Dokumenten suchen …'}
          className="min-w-0 flex-1"
        />
      </div>

      <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-300 p-2">
        {others.map((item) => (
          <Checkbox
            key={item.id}
            label={
              <span>
                {item.title}
                {item.hint && <span className="text-xs text-slate-400"> · {item.hint}</span>}
              </span>
            }
            checked={false}
            onChange={() => onChange([...value, { kind, refId: item.id, title: item.title }])}
          />
        ))}
        {others.length === 0 && !treffer.isLoading && (
          <p className="p-1 text-sm text-slate-500">
            {search.trim() ? 'Keine Datei gefunden.' : 'Keine weiteren Dateien.'}
          </p>
        )}
      </div>
      {treffer.data && treffer.data.total > treffer.data.items.length && (
        <p className="hint">
          {treffer.data.items.length} von {treffer.data.total} – zum Eingrenzen suchen
        </p>
      )}
      </>
      )}
    </div>
  );
}

/** Anhaenge anzeigen, optional mit Entfernen. */
export function AttachmentList({
  attachments,
  onRemove,
  removing,
}: {
  attachments: AttachmentDto[];
  onRemove?: (attachment: AttachmentDto) => void;
  removing?: boolean;
}): JSX.Element {
  if (attachments.length === 0) {
    return <p className="text-sm text-slate-500">Keine Anhänge.</p>;
  }
  return (
    <ul className="divide-y divide-slate-100">
      {attachments.map((attachment) => {
        const size = formatSize(attachment.sizeBytes);
        return (
          <li key={`${attachment.kind}:${attachment.refId}`} className="flex flex-wrap items-center gap-2 py-2">
            <span aria-hidden>{KIND_ICONS[attachment.kind]}</span>
            <div className="min-w-0 flex-1">
              {attachment.available && attachment.downloadUrl ? (
                <a
                  href={attachment.downloadUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-sm font-medium text-brand-700 hover:underline"
                >
                  {attachment.title}
                </a>
              ) : (
                <span className={cn('text-sm', attachment.available ? 'text-slate-700' : 'text-slate-400 line-through')}>
                  {attachment.title}
                </span>
              )}
              <p className="text-xs text-slate-500">
                {[
                  attachment.kind === 'MEDIA' ? 'Mediathek' : 'Dokument',
                  attachment.fileName,
                  size,
                  attachment.available ? (attachment.isExternal ? 'Link' : null) : 'nicht mehr verfügbar',
                ].filter(Boolean).join(' · ')}
              </p>
            </div>
            {onRemove && (
              <button
                type="button"
                className="text-xs text-slate-400 hover:text-rose-600"
                disabled={removing}
                onClick={() => onRemove(attachment)}
              >
                entfernen
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
